import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import { Glossary } from "src/components/Text/Glossary";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { formatUSPhoneNumber } from "src/services/format";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";

type Organization = { path: string };

interface DuplicateParentCardProps {
  parent: ParentSummary;
  organization: RD.RemoteData<unknown, Organization>;
  // Select this existing parent in place instead of navigating away. Its id is
  // used to link the students being added.
  onUse: (parent: ParentSummary) => void;
}

export function DuplicateParentCard({
  parent,
  organization,
  onUse,
}: DuplicateParentCardProps) {
  const fullName = [parent.first_name, parent.last_name]
    .filter(Boolean)
    .join(" ");
  const contactParts = [
    parent.phone_number ? formatUSPhoneNumber(parent.phone_number) : null,
    parent.email_address,
  ].filter(Boolean);

  return (
    <Box
      border="1px solid"
      borderColor="orange.200"
      borderLeft="4px solid"
      borderLeftColor="orange.400"
      borderRadius="md"
      padding={4}
      backgroundColor="white"
    >
      <Flex
        alignSelf="flex-start"
        display="inline-flex"
        align="flex-start"
        gap={2}
        maxW="100%"
        backgroundColor="orange.100"
        color="orange.800"
        borderRadius="md"
        paddingX={3}
        paddingY={1}
        marginBottom={3}
      >
        <Box
          width={2}
          height={2}
          mt={1.5}
          flexShrink={0}
          borderRadius="full"
          backgroundColor="orange.500"
        />
        <Text fontSize="sm" fontWeight="600" minW={0}>
          Possible match: email or phone
        </Text>
      </Flex>

      <Text fontSize="lg" fontWeight="700">
        {fullName}
      </Text>

      {contactParts.length > 0 && (
        <Text fontSize="sm" color="gray.700">
          {contactParts.join(" · ")}
        </Text>
      )}

      {parent.children.length > 0 && (
        <Text fontSize="sm" color="gray.700">
          Parent of{" "}
          {parent.children.map((child, index) => (
            <React.Fragment key={child.id}>
              {index > 0 && ", "}
              <PersonNameLink
                to={Url.OrgAdmin.Students.edit(organization, child.id)}
              >
                {[child.first_name, child.last_name].filter(Boolean).join(" ")}
              </PersonNameLink>
            </React.Fragment>
          ))}
        </Text>
      )}

      <Text fontSize="sm" color="gray.700">
        ID: {parent.id}
      </Text>

      <Button
        type="button"
        size="sm"
        marginTop={4}
        onClick={() => onUse(parent)}
      >
        <Glossary>Use this parent</Glossary>
      </Button>
    </Box>
  );
}
